import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { MODULES } from "@/utils/permissions";
import NotFound from '@/pages/NotFound';
import ProtectedRoute from '@/components/feature/ProtectedRoute';
import LoginPage from '@/pages/login/page';
import ForgetPasswordPage from '@/pages/forgot-password/page';
import Dashboard from '@/pages/dashboard/page';
import ReportsLanding from '@/pages/reports/page';
import Settings from '@/pages/settings/page';
import Users from '@/pages/users/page';
import WarehousesPage from '@/pages/masters/warehouses/page';
import PartiesPage from '@/pages/masters/parties/page';
import ItemsPage from '@/pages/masters/items/page';
import CategoriesUnitsPage from '@/pages/masters/categories/page';
import WorkCentersPage from '@/pages/masters/work-center/page';
import MachinesPage from '@/pages/masters/machines/page';
import ShiftsPage from '@/pages/masters/shifts/page';
import OperatorsPage from '@/pages/masters/operators/page';
import CostCentersPage from '@/pages/masters/cost-center/page';
import InspectionChecklistsPage from '@/pages/masters/inspection-checklists/page';
import DowntimeCodesPage from '@/pages/masters/downtime-codes/page';
import QualityParametersPage from '@/pages/masters/quality-parameters/page';
import RejectionCodesPage from '@/pages/masters/rejection-codes/page';
import RoutingPage from '@/pages/masters/routing/page';
import RoutingFormPage from '@/pages/masters/routing/form/page';
import SalesInvoiceListPage from '@/pages/sales/invoices/page';
import SalesInvoiceNewPage from '@/pages/sales/invoices/new/page';
import SaleReturnsPage from '@/pages/sales/returns/page';
import ChallansPage from '@/pages/sales/challans/page';
import SalesOrdersPage from '@/pages/sales/orders/page';
import SalesOrderNewPage from '@/pages/sales/orders/new/page';
import SalesOrderDetailPage from '@/pages/sales/orders/[id]/page';
import SalesOrderReportPage from '@/pages/sales/orders/report/page';
import QuotationsPage from '@/pages/sales/quotations/page';
import QuotationNewPage from '@/pages/sales/quotations/new/page';
import QuotationDetailPage from '@/pages/sales/quotations/[id]/page';
import ProformaListPage from '@/pages/sales/proforma/page';
import ProformaNewPage from '@/pages/sales/proforma/new/page';
import ProformaDetailPage from '@/pages/sales/proforma/[id]/page';
import SalesPaymentPage from '@/pages/sales/payments/page';
import SalesPaymentsHistoryPage from '@/pages/sales/payments/history/page';
import PurchasePaymentPage from '@/pages/purchase/payments/page';
import PurchasePaymentsHistoryPage from '@/pages/purchase/payments/history/page';
import PurchaseInvoiceListPage from '@/pages/purchase/invoices/page';
import PurchaseInvoiceNewPage from '@/pages/purchase/invoices/new/page';
import PurchaseReturnsPage from '@/pages/purchase/returns/page';
import PurchaseApprovalsPage from '@/pages/purchase/approvals/page';
import GRNListPage from '@/pages/purchase/grn/page';
import PurchaseOrdersPage from '@/pages/purchase/orders/page';
import PurchaseOrderNewPage from '@/pages/purchase/orders/new/page';
import StockViewPage from '@/pages/inventory/stock/page';
import StockReceivingPage from '@/pages/inventory/receiving/page';
import StockTransferPage from '@/pages/inventory/transfer/page';
import StockAdjustmentPage from '@/pages/inventory/adjustment/page';
import StockEntriesPage from '@/pages/inventory/stock-entries/page';
import BarcodeManagementPage from '@/pages/inventory/barcode-management/page';
import SerialsPage from '@/pages/inventory/serials/page';
import SerialDetailPage from '@/pages/inventory/serials/SerialDetailPage';
import SerialTraceabilityPage from '@/pages/inventory/serials/SerialTraceabilityPage';

// compliance 
import EWayBillPage from '@/pages/compliance/eway-bill/page';
import EWayBillSettingsPage from '@/pages/compliance/eway-bill/settings/page';
import EInvoicePage from '@/pages/compliance/e-invoice/page';
import EInvoiceSettingsPage from '@/pages/compliance/e-invoice/settings/page';
import PublicEInvoiceVerifyPage from '@/pages/verify/e-invoice/page';
import TDSPage from '@/pages/compliance/tds/page';
import TDSSettingsPage from '@/pages/compliance/tds/settings/page';

// Manufacturing 
import BOMListTable from '@/pages/manufacturing/bom/page';
import BOMForm from '@/pages/manufacturing/bom/BOMForm';
import ProductionOrder from '@/pages/manufacturing/production-orders/page';
import ProductionOrderForm from '@/pages/manufacturing/production-orders/ProductionOrderForm';
import ProductionOrderDetailPage from '@/pages/manufacturing/production-orders/detail/page';
import IssueQueuePage from '@/pages/manufacturing/issue-queue/page';
import WorkOrdersPage from '@/pages/manufacturing/work-orders/page';
import MyWorkOrdersPage from '@/pages/manufacturing/my-work-orders/page';
import ProductionSchedulePage from '@/pages/manufacturing/schedule/page';
import MRPRunPage from '@/pages/manufacturing/mrp/page';
import MaterialShortagePage from '@/pages/manufacturing/mrp/shortage/page';
import ProductionEntryPage from '@/pages/manufacturing/shop-floor/page';
import DowntimeEntryPage from '@/pages/manufacturing/downtime/page';
import LiveDashboardPage from '@/pages/manufacturing/live/page';
import PurchaseSuggestionsPage from '@/pages/manufacturing/mrp/purchase/page';
import CostingDashboardPage from '@/pages/manufacturing/costing/page';
import CostingSetupPage from '@/pages/manufacturing/costing/setup/page';
import OrderCostingPage from '@/pages/manufacturing/costing/orders/page';
import VariancePage from '@/pages/manufacturing/costing/variance/page';
import StandardCostPage from '@/pages/manufacturing/costing/standard/page';
import ActualCostPage from '@/pages/manufacturing/costing/actual/page';
import JobWorkListPage from '@/pages/manufacturing/jobwork/page';
import JobWorkNewPage from '@/pages/manufacturing/jobwork/new/page';
import JobWorkDetailPage from '@/pages/manufacturing/jobwork/[id]/page';
import ITC04ReportPage from '@/pages/manufacturing/jobwork/itc04/page';
import BatchTraceabilityPage from '@/pages/manufacturing/traceability/page';
import ForwardTracePage from '@/pages/manufacturing/traceability/forward/page';
import BackwardTracePage from '@/pages/manufacturing/traceability/backward/page';
import RecallSimulationPage from '@/pages/manufacturing/traceability/recall/page';
import ToolListPage from '@/pages/manufacturing/tools/page';
import ToolNewPage from '@/pages/manufacturing/tools/new/page';
import ToolDetailPage from '@/pages/manufacturing/tools/[id]/page';
import MaintenancePage from '@/pages/manufacturing/maintenance/page';
import MaintenanceScheduleNewPage from '@/pages/manufacturing/maintenance/new/page';
import MaintenanceAMCNewPage from '@/pages/manufacturing/maintenance/amc/new/page';
import MaintenanceMWOPage from '@/pages/manufacturing/maintenance/workorders';
import MaintenanceSchedulesPage from '@/pages/manufacturing/maintenance/schedules';
import MaintenanceCalendarPage from '@/pages/manufacturing/maintenance/calendar';
import IncomingQCPage from '@/pages/manufacturing/qc/incoming/page';
import InProcessQCPage from '@/pages/manufacturing/qc/inprocess/page';
import FinalQCPage from '@/pages/manufacturing/qc/final/page';
import NCRCAPAPage from '@/pages/manufacturing/qc/ncr/page';

// Finance
import ChartOfAccountsPage from '@/pages/finance/accounts/page';
import JournalVoucherListPage from '@/pages/finance/journal/page';
import NewJournalVoucherPage from '@/pages/finance/journal/new/page';
import JVDetailPage from '@/pages/finance/journal/[id]/page';
import TrialBalancePage from '@/pages/finance/trial-balance/page';
import ProfitLossPage from '@/pages/finance/reports/ProfitLossPage';
import BalanceSheetPage from '@/pages/finance/reports/BalanceSheetPage';
import AccountLedgerPage from '@/pages/finance/ledger/page';
import BankReconciliationPage from '@/pages/finance/bank-reconciliation/page';
// Fixed Assets
import FixedAssetListPage from '@/pages/finance/assets/page';
import FixedAssetDetailPage from '@/pages/finance/assets/[id]/page';
import DepreciationPage from '@/pages/finance/assets/depreciation/page';
import NewAssetPage from '@/pages/finance/assets/new/page';
// ── Gate Pass pages ───────────────────────────────────────────────────────────
import OutwardGatePassPage from '@/pages/inventory/gate-pass/outward/page';
import InwardGatePassPage from '@/pages/inventory/gate-pass/inward/page';

// ── Guard pages ───────────────────────────────────────────────────────────────
import GuardDashboard from '@/pages/guard/dashboard/page';
import GuardOutwardPage from '@/pages/guard/outward/page';
import GuardInwardPage from '@/pages/guard/inward/page';

// Reports
import ExecutiveDashboardPage from '@/pages/reports/dashboard/page';
import StockSummaryReport from '@/pages/reports/stock-summary/page';
import StockLedgerReport from '@/pages/reports/stock-ledger/page';
import LowStockReport from '@/pages/reports/low-stock/page';
import PurchaseRegisterReport from '@/pages/reports/purchase-register/page';
import GSTPurchaseReport from '@/pages/reports/gst-purchase/page';
import SalesRegisterReport from '@/pages/reports/sales-register/page';
import GSTSalesReport from '@/pages/reports/gst-sales/page';
import OutstandingReport from '@/pages/reports/outstanding/page';
import DayBookReport from '@/pages/reports/day-book/page';
import PartyLedgerReport from '@/pages/reports/party-ledger/page';
import ManufacturingReportsPage from '@/pages/reports/manufacturing/page';
import CostCenterReportPage from '@/pages/reports/cost-centers/page';
import ScrapReportPage from '@/pages/reports/scrap/page';
import ScrapManagementPage from '@/pages/manufacturing/scrap/page';
import ScrapYardPage from '@/pages/inventory/scrap-yard/page';
import FinancialReportsPage from '@/pages/reports/financial/page';
import GSTReportsPage from '@/pages/reports/gst/page';
import AIForecastingPage from '@/pages/reports/forecasting/page';
import ResetPasswordPage from '@/pages/reset-password/page';

import { useAuth } from "@/contexts/AuthContext";
import EditPOPage from '@/pages/purchase/orders/new/EditPOPage';
import PaymentDuesTable from '@/pages/purchase/payments/components/paymantDuesTable';
import ApprovalSettingsPage from '@/pages/purchase/approvals/settings/page';
import CurrencyPage from '@/pages/finance/currencies/page';

const P = ({
  children,
  roles,
  requirePermission,
}: {
  children: React.ReactNode;
  roles?: string[];
  requirePermission?: () => boolean;
}) => (
  <ProtectedRoute requiredRoles={roles} requirePermission={requirePermission}>
    {children}
  </ProtectedRoute>
);

const GuardOnly = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute requiredRoles={['SECURITY_GUARD'] as never}>{children}</ProtectedRoute>
);

/**
 * Perm — thin wrapper that reads hasPermission from context and passes it as
 * requirePermission to ProtectedRoute. This replaces the broken
 * `<P module={…} action={…}>` pattern that ProtectedRoute never actually supported.
 *
 * Usage:
 *   <Perm module={MODULES.WAREHOUSES} action="view"><WarehousesPage /></Perm>
 */
const Perm = ({
  children,
  module,
  action,
  override,
}: {
  children: React.ReactNode;
  module: string;
  action: 'create' | 'view' | 'edit' | 'delete';
  override?: (args: {
    hasPermission: (m: string, a: 'create' | 'view' | 'edit' | 'delete') => boolean;
    hasControl: (c: string) => boolean;
  }) => boolean;
}) => {
  const { hasPermission, hasControl, user } = useAuth();

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const allowed =
    isSuperAdmin
      ? true
      : override
        ? override({ hasPermission, hasControl })
        : hasPermission(module, action);

  return (
    <ProtectedRoute requirePermission={() => allowed}>
      {children}
    </ProtectedRoute>
  );
};



// ── Route table ───────────────────────────────────────────────────────────────

const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgetPasswordPage /> },
  { path: '/reset-password/:token', element: <ResetPasswordPage /> },
  { path: '/verify/e-invoice/:irn', element: <PublicEInvoiceVerifyPage /> },
  { path: '/verify/einvoice/:irn', element: <PublicEInvoiceVerifyPage /> },
  { path: '/verify/invoice/:irn', element: <PublicEInvoiceVerifyPage /> },

  { path: '/', element: <P><Dashboard /></P> },
  {
    path: '/settings',
    element: (
      <Perm
        module={MODULES.SETTINGS}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.SETTINGS, "view") ||
          hasControl("manageUserPermissions")
        }
      >
        <Settings />
      </Perm>
    ),
  },
  {
    path: '/users',
    element: (
      <Perm
        module={MODULES.USERS}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.USERS, "view") ||
          hasControl("manageUserPermissions")
        }
      >
        <Users />
      </Perm>
    ),
  },

  // ── Masters ───────────────────────────────────────────────────────────────
  { path: '/masters', element: <Navigate to="/masters/company" replace /> },

  {
    path: '/masters/warehouses',
    element: (
      <Perm module={MODULES.WAREHOUSES} action="view">
        <WarehousesPage />
      </Perm>
    ),
  },
  {
    path: '/masters/parties',
    element: (
      <Perm module={MODULES.PARTIES} action="view">
        <PartiesPage />
      </Perm>
    ),
  },
  { path: '/masters/items', element: <P><ItemsPage /></P> },
  {
    path: '/masters/categories', element: (
      <Perm module={MODULES.CATEGORIES} action="view">
        <CategoriesUnitsPage />
      </Perm>
    )
  },
  {
    path: '/masters/shifts',
    element: (
      <Perm module={MODULES.SHIFT} action="view">
        <ShiftsPage />
      </Perm>
    ),
  },
  {
    path: '/masters/operators',
    element: (
      <Perm module={MODULES.OPERATOR} action="view">
        <OperatorsPage />
      </Perm>
    ),
  },
  {
    path: '/masters/machines',
    element: (
      <Perm module={MODULES.MACHINE} action="view">
        <MachinesPage />
      </Perm>
    ),
  },
  {
    path: '/masters/work-centers',
    element: (
      <Perm module={MODULES.WORK_CENTERS} action="view">
        <WorkCentersPage />
      </Perm>
    ),
  },
  {
    path: '/masters/cost-centers',
    element: (
      <Perm module={MODULES.COST_CENTRE} action="view">
        <CostCentersPage />
      </Perm>
    ),
  },
  {
    path: '/masters/inspection-checklists',
    element: (
      <Perm module={MODULES.INSPECTION_CHECKLIST} action="view">
        <InspectionChecklistsPage />
      </Perm>
    ),
  },

  {
    path: 'masters/downtime-codes',
    element: (
      <Perm module={MODULES.DOWNTIME_CODE} action="view">
        <DowntimeCodesPage />
      </Perm>
    ),
  },

  {
    path: 'masters/quality-parameters',
    element: (
      <Perm module={MODULES.QUALITY_PARAMETER} action="view">
        <QualityParametersPage />
      </Perm>
    ),
  },

  {
    path: 'masters/rejection-codes',
    element: (
      <Perm module={MODULES.REJECTION_CODE} action="view">
        <RejectionCodesPage />
      </Perm>
    ),
  },
  {
    path: 'masters/routing',
    element: (
      <Perm module={MODULES.ROUTING} action="view">
        <RoutingPage />
      </Perm>
    ),
  },
  {
    path: 'masters/routing/new',
    element: (
      <Perm module={MODULES.ROUTING} action="create">
        <RoutingFormPage />
      </Perm>
    ),
  },
  {
    path: 'masters/routing/:id/edit',
    element: (
      <Perm module={MODULES.ROUTING} action="edit">
        <RoutingFormPage />
      </Perm>
    ),
  },

  // ── Sales ─────────────────────────────────────────────────────────────────
  { path: '/sales', element: <Navigate to="/sales/invoices" replace /> },
  {
    path: '/sales/invoices',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <SalesInvoiceListPage />
      </Perm>
    ),
  },
  { path: '/sales/invoices/new', element: <P><SalesInvoiceNewPage /></P> },
  {
    path: '/sales/returns',
    element: (
      <Perm module={MODULES.SALE_RETURN} action="view">
        <SaleReturnsPage />
      </Perm>
    ),
  },
  {
    path: '/sales/challans',
    element: (
      <Perm module={MODULES.CHALLAN} action="view">
        <ChallansPage />
      </Perm>
    ),
  },
  {
    path: '/sales/orders',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <SalesOrdersPage />
      </Perm>
    ),
  },
  {
    path: '/sales/orders/new',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <SalesOrderNewPage />
      </Perm>
    ),
  },
  {
    path: '/sales/orders/:id',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <SalesOrderDetailPage />
      </Perm>
    ),
  },
  {
    path: '/sales/orders/report',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <SalesOrderReportPage />
      </Perm>
    ),
  },
  {
    path: '/sales/quotations',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <QuotationsPage />
      </Perm>
    ),
  },
  {
    path: '/sales/quotations/new',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <QuotationNewPage />
      </Perm>
    ),
  },
  {
    path: '/sales/quotations/:id',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <QuotationDetailPage />
      </Perm>
    ),
  },
  {
    path: '/sales/proforma',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <ProformaListPage />
      </Perm>
    ),
  },
  {
    path: '/sales/proforma/new',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <ProformaNewPage />
      </Perm>
    ),
  },
  {
    path: '/sales/proforma/:id',
    element: (
      <Perm module={MODULES.SALES_INVOICE} action="view">
        <ProformaDetailPage />
      </Perm>
    ),
  },
  {
    path: '/sales/payments',
    element: (
      <Perm module={MODULES.SALES_PAYMENT} action="view">
        <SalesPaymentsHistoryPage />
      </Perm>
    ),
  },
  { path: '/sales/payments/new', element: <P><SalesPaymentPage /></P> },

  // ── Purchase ──────────────────────────────────────────────────────────────
  { path: '/purchase', element: <Navigate to="/purchase/orders" replace /> },
  {
    path: '/purchase/orders',
    element: (
      <Perm module={MODULES.PURCHASE_ORDER} action="view">
        <PurchaseOrdersPage />
      </Perm>
    ),
  },
  {
    path: '/purchase/orders/:id',
    element: (
      <Perm module={MODULES.PURCHASE_ORDER} action="view">
        <PurchaseOrdersPage />
      </Perm>
    ),
  },
  {
    path: '/purchase/orders/:id/edit',
    element: (
      <Perm module={MODULES.PURCHASE_ORDER} action="edit">
        <EditPOPage />
      </Perm>
    ),
  },
  {
    path: '/purchase/approvals',
    element: (
      <Perm module={MODULES.PURCHASE_APPROVAL} action="view">
        <PurchaseApprovalsPage />
      </Perm>
    )
  },
  {
    path: "/purchase/approvals/settings",
    element: (
      <Perm module={MODULES.PURCHASE_APPROVAL} action="view">
        <ApprovalSettingsPage />
      </Perm>
    )
  },
  { path: "/purchase-orders/edit/:id", element: <EditPOPage /> },
  { path: '/purchase/orders/new', element: <P><PurchaseOrderNewPage /></P> },
  {
    path: '/purchase/invoices',
    element: (
      <Perm module={MODULES.PURCHASE_INVOICE} action="view">
        <PurchaseInvoiceListPage />
      </Perm>
    ),
  },
  { path: '/purchase/invoices/new', element: <P><PurchaseInvoiceNewPage /></P> },
  {
    path: '/purchase/payments',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <PurchasePaymentsHistoryPage />
      </Perm>
    ),
  },
  { path: '/purchase/payments/new', element: <P><PurchasePaymentPage /></P> },
  { path: '/purchase/payments/news', element: <P><PaymentDuesTable /></P> },
  {
    path: '/purchase/grn',
    element: (
      <Perm module={MODULES.GRN_HISTORY} action="view">
        <GRNListPage />
      </Perm>
    ),
  },
  {
    path: '/purchase/returns',
    element: (
      <Perm module={MODULES.PURCHASE_RETURN} action="view">
        <PurchaseReturnsPage />
      </Perm>
    ),
  },

  // ── Inventory ─────────────────────────────────────────────────────────────
  { path: '/inventory', element: <Navigate to="/inventory/stock" replace /> },
  { path: '/inventory/stock', element: <P><StockViewPage /></P> },
  { path: '/inventory/serials', element: <P><SerialsPage /></P> },
  { path: '/inventory/serials/:id', element: <P><SerialDetailPage /></P> },
  { path: '/inventory/serial-tracking', element: <P><SerialTraceabilityPage /></P> },
  {
    path: '/inventory/receiving',
    element: (
      <Perm module={MODULES.STOCK_RECEIVING} action="view">
        <StockReceivingPage />
      </Perm>
    ),
  },
  {
    path: '/inventory/transfer',
    element: (
      <Perm module={MODULES.STOCK_TRANSFER} action="view">
        <StockTransferPage />
      </Perm>
    ),
  },
  // {
  //   path: '/inventory/adjustment',
  //   element: (
  //     <Perm module={MODULES.STOCK_ADJUSTMENT} action="view">
  //       <StockAdjustmentPage />
  //     </Perm>
  //   ),
  // },
  {
    path: '/inventory/adjustment',
    element: (
      <Perm
        module={MODULES.STOCK_ADJUSTMENT}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.STOCK_ADJUSTMENT, "view") ||
          hasControl("approveStockAdjustment")
        }
      >
        <StockAdjustmentPage />
      </Perm>
    ),
  },

  {
    path: '/inventory/stock-entries',
    element: (
      <Perm module={MODULES.STOCK_ENTRIES} action="view">
        <StockEntriesPage />
      </Perm>
    ),
  },

  // -- compliance 
  {
    path: '/compliance',
    element: <Navigate to="/compliance/eway-bill" replace />
  },
  {
    path: '/compliance/eway-bill',
    element: <Perm module={MODULES.EWAY_BILL} action="view"><EWayBillPage /></Perm>
  },
  {
    path: '/compliance/eway-bill/settings',
    element: <Perm module={MODULES.EWAY_BILL} action="view"><EWayBillSettingsPage /></Perm>
  },
  {
    path: '/compliance/e-invoice',
    element: <Perm module={MODULES.E_INVOICE} action="view"><EInvoicePage /></Perm>
  },
  {
    path: '/compliance/e-invoice/settings',
    element: <Perm module={MODULES.E_INVOICE} action="view"><EInvoiceSettingsPage /></Perm>
  },
  {
    path: '/compliance/tds',
    element: <Perm module={MODULES.TDS} action="view"><TDSPage /></Perm>
  },
  {
    path: '/compliance/tds/settings',
    element: <Perm module={MODULES.TDS} action="view"><TDSSettingsPage /></Perm>
  },

  // ── Gate Pass (under Inventory — non-guard users) ─────────────────────────
  {
    path: '/inventory/gate-pass',
    element: <Navigate to="/inventory/gate-pass/outward" replace />,
  },
  {
    path: '/inventory/gate-pass/outward',
    element: (
      <Perm module={MODULES.GATE_PASS_OUTWARD} action="view">
        <OutwardGatePassPage />
      </Perm>
    ),
  },
  {
    path: '/inventory/gate-pass/inward',
    element: (
      <Perm module={MODULES.GATE_PASS_INWARD} action="view">
        <InwardGatePassPage />
      </Perm>
    ),
  },

  // ── Print ─────────────────────────────────────────────────────────────────
  { path: '/print', element: <Navigate to="/print/barcode-management" replace /> },
  { path: '/print/barcode-management', element: <P><BarcodeManagementPage /></P> },
  // Legacy redirect
  { path: '/inventory/barcode-management', element: <Navigate to="/print/barcode-management" replace /> },

  // manufacturing end navigation 
  {
    path: '/manufacturing/create-bom',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <BOMForm />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/bom/:id/edit',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="edit">
        <BOMForm />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/routing',
    element: (
      <Perm module={MODULES.ROUTING} action="view">
        <RoutingPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/routing/new',
    element: (
      <Perm module={MODULES.ROUTING} action="create">
        <RoutingFormPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/routing/:id/edit',
    element: (
      <Perm module={MODULES.ROUTING} action="edit">
        <RoutingFormPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/production-orders',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <ProductionOrder />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/production-orders/new',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <ProductionOrderForm />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/production-orders/:id',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <ProductionOrderDetailPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/production-orders/:id/edit',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="edit">
        <ProductionOrderForm />
      </Perm>
    ),
  },
  { path: '/manufacturing/orders', element: <Navigate to="/manufacturing/production-orders" replace /> },
  { path: '/manufacturing/orders/new', element: <Navigate to="/manufacturing/production-orders/new" replace /> },
  { path: '/manufacturing/orders/:id', element: <Navigate to="/manufacturing/production-orders" replace /> },
  { path: '/manufacturing/orders/:id/edit', element: <Navigate to="/manufacturing/production-orders" replace /> },
  // Sidebar section headers (expand/collapse only) — no page of their own, land on the first screen of the section.
  { path: '/manufacturing', element: <Navigate to="/manufacturing/live" replace /> },
  { path: '/manufacturing/production', element: <Navigate to="/manufacturing/production-orders" replace /> },
  { path: '/manufacturing/quality', element: <Navigate to="/manufacturing/qc/incoming" replace /> },
  {
    path: '/manufacturing/issue-queue',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <IssueQueuePage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/my-work-orders',
    element: (
      <P>
        <MyWorkOrdersPage />
      </P>
    ),
  },
  {
    path: '/manufacturing/work-orders',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <WorkOrdersPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/schedule',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <ProductionSchedulePage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/mrp',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <MRPRunPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/mrp/shortage',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <MaterialShortagePage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/mrp/purchase',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <PurchaseSuggestionsPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/shop-floor',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <ProductionEntryPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/downtime',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <DowntimeEntryPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/live',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <LiveDashboardPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/qc/incoming',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <IncomingQCPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/qc/inprocess',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <InProcessQCPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/qc/final',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <FinalQCPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/qc/ncr',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <NCRCAPAPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/bom',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <BOMListTable />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/bom-list',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <BOMListTable />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/costing',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <CostingDashboardPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/costing/setup',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <CostingSetupPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/costing/orders',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <OrderCostingPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/costing/variance',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <VariancePage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/costing/standard',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <StandardCostPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/costing/actual',
    element: (
      <Perm module={MODULES.PURCHASE_PAYMENT} action="view">
        <ActualCostPage />
      </Perm>
    ),
  },
  {
    path: '/manufacturing/jobwork',
    element: (
      <Perm module={MODULES.JOB_WORK} action="view">
        <JobWorkListPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/jobwork/new',
    element: (
      <Perm module={MODULES.JOB_WORK} action="view">
        <JobWorkNewPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/jobwork/itc04',
    element: (
      <Perm module={MODULES.JOB_WORK} action="view">
        <ITC04ReportPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/jobwork/:id',
    element: (
      <Perm module={MODULES.JOB_WORK} action="view">
        <JobWorkDetailPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/traceability',
    element: (
      <Perm module={MODULES.TRACEABILITY} action="view">
        <BatchTraceabilityPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/traceability/forward',
    element: (
      <Perm module={MODULES.TRACEABILITY} action="view">
        <ForwardTracePage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/traceability/backward',
    element: (
      <Perm module={MODULES.TRACEABILITY} action="view">
        <BackwardTracePage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/traceability/recall',
    element: (
      <Perm module={MODULES.TRACEABILITY} action="view">
        <RecallSimulationPage />
      </Perm>
    )
  }, {
    path: '/manufacturing/tools',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <ToolListPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/tools/new',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <ToolNewPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/tools/:id',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <ToolDetailPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenancePage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance/new',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenanceScheduleNewPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance/schedules/new',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenanceScheduleNewPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance/amc/new',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenanceAMCNewPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance/workorders',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenanceMWOPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance/schedules',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenanceSchedulesPage />
      </Perm>
    )
  },
  {
    path: '/manufacturing/maintenance/calendar',
    element: (
      <Perm module={MODULES.TOOL} action="view">
        <MaintenanceCalendarPage />
      </Perm>
    )
  },

  // Finanace 
  {
    path: '/finance',
    element: <Navigate to='/finance/accounts' replace />
  },
  {
    path: '/finance/accounts',
    element: (
      <Perm module={MODULES.CHART_OF_ACCOUNTS} action="view">
        <ChartOfAccountsPage />
      </Perm>
    )
  },
  {
    path: '/finance/journal',
    element:
      (
        <Perm module={MODULES.JOURNAL_VOUCHER} action="view">
          <JournalVoucherListPage />
        </Perm>
      )
  },
  {
    path: '/finance/journal/new',
    element: (
      <Perm module={MODULES.JOURNAL_VOUCHER} action="create">
        <NewJournalVoucherPage />
      </Perm>
    )
  },
  {
    path: '/finance/journal/:id',
    element: (
      <Perm module={MODULES.JOURNAL_VOUCHER} action="view">
        <JVDetailPage />
      </Perm>
    )
  },
  {
    path: '/finance/trial-balance',
    element: (
      <Perm module={MODULES.TRIAL_BALANCE} action="view">
        <TrialBalancePage />
      </Perm>
    )
  },
  {
    path: '/finance/ledger',
    element: (
      <Perm module={MODULES.ACCOUNT_LEDGER} action="view">
        <AccountLedgerPage />
      </Perm>
    )
  },
  {
    path: '/finance/reports/profit-loss',
    element: (
      <Perm
        module={MODULES.PROFIT_LOSS}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.PROFIT_LOSS, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <ProfitLossPage />
      </Perm>
    )
  },
  {
    path: '/finance/pnl',
    element: (
      <Perm
        module={MODULES.PROFIT_LOSS}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.PROFIT_LOSS, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <ProfitLossPage />
      </Perm>
    )
  },
  {
    path: '/finance/reports/balance-sheet',
    element: (
      <Perm
        module={MODULES.BALANCE_SHEET}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.BALANCE_SHEET, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <BalanceSheetPage />
      </Perm>
    )
  },
  {
    path: '/finance/balance-sheet',
    element: (
      <Perm
        module={MODULES.BALANCE_SHEET}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.BALANCE_SHEET, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <BalanceSheetPage />
      </Perm>
    )
  },
  {
    path: '/finance/bank-reconciliation',
    element: (
      <Perm module={MODULES.BANK_RECONCILIATION} action="view">
        <BankReconciliationPage />
      </Perm>
    )
  },
  {
    path: '/finance/assets',
    element: (
      <Perm module={MODULES.FIXED_ASSET} action="view">
        <FixedAssetListPage />
      </Perm>
    )
  },
  {
    path: '/finance/assets/new',
    element: (
      <Perm module={MODULES.FIXED_ASSET} action="create">
        <NewAssetPage />
      </Perm>
    )
  },
  {
    path: '/finance/assets/depreciation',
    element: (
      <Perm module={MODULES.FIXED_ASSET_DEPRECIATION} action="view">
        <DepreciationPage />
      </Perm>
    )
  },
  {
    path: '/finance/assets/:id',
    element: (
      <Perm module={MODULES.FIXED_ASSET} action="view">
        <FixedAssetDetailPage />
      </Perm>
    )
  },
  {
    path: '/finance/currencies',
    element: (
      <Perm module={MODULES.CURRENCY} action="view">
        <CurrencyPage />
      </Perm>
    )
  },

  // ── Guard-only routes (/guard/*) ──────────────────────────────────────────
  { path: '/guard', element: <Navigate to="/guard/dashboard" replace /> },
  { path: '/guard/dashboard', element: <GuardOnly><GuardDashboard /></GuardOnly> },
  { path: '/guard/outward', element: <GuardOnly><GuardOutwardPage /></GuardOnly> },
  { path: '/guard/inward', element: <GuardOnly><GuardInwardPage /></GuardOnly> },

  // ── Reports ───────────────────────────────────────────────────────────────
  { path: '/reports', element: <P><ReportsLanding /></P> },
  {
    path: '/reports/dashboard',
    element: (
      <Perm
        module={MODULES.REPORT_DASHBOARD}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_DASHBOARD, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <ExecutiveDashboardPage />
      </Perm>
    ),
  },
  {
    path: '/reports/stock-summary',
    element: (
      <Perm
        module={MODULES.REPORT_STOCK_SUMMARY}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_STOCK_SUMMARY, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <StockSummaryReport />
      </Perm>
    ),
  },
  {
    path: '/reports/party-ledger',
    element: (
      <Perm
        module={MODULES.REPORT_PARTY_LEDGER}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_PARTY_LEDGER, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <PartyLedgerReport />
      </Perm>
    ),
  },
  {
    path: '/reports/low-stock',
    element: (
      <Perm
        module={MODULES.REPORT_LOW_STOCK}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_LOW_STOCK, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <LowStockReport />
      </Perm>
    ),
  },
  {
    path: '/reports/purchase-register',
    element: (
      <Perm
        module={MODULES.REPORT_PURCHASE_REG}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_PURCHASE_REG, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <PurchaseRegisterReport />
      </Perm>
    ),
  },
  {
    path: '/reports/gst-purchase',
    element: (
      <Perm
        module={MODULES.REPORT_GST_PURCHASE}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_GST_PURCHASE, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <GSTPurchaseReport />
      </Perm>
    ),
  },
  {
    path: '/reports/stock-ledger',
    element: (
      <Perm
        module={MODULES.REPORT_STOCK_LEDGER}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_STOCK_LEDGER, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <StockLedgerReport />
      </Perm>
    ),
  },
  {
    path: '/reports/day-book',
    element: (
      <Perm
        module={MODULES.REPORT_DAY_BOOK}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_DAY_BOOK, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <DayBookReport />
      </Perm>
    ),
  },
  {
    path: '/reports/outstanding',
    element: (
      <Perm
        module={MODULES.REPORT_OUTSTANDING}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_OUTSTANDING, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <OutstandingReport />
      </Perm>
    ),
  },
  {
    path: '/reports/gst-sales',
    element: (
      <Perm
        module={MODULES.REPORT_GST_SALES}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_GST_SALES, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <GSTSalesReport />
      </Perm>
    ),
  },
  {
    path: '/reports/sales-register',
    element: (
      <Perm
        module={MODULES.REPORT_SALES_REG}
        action="view"
        override={({ hasPermission, hasControl }) =>
          hasPermission(MODULES.REPORT_SALES_REG, "view") ||
          hasControl('viewFinancialReports')
        }
      >
        <SalesRegisterReport />
      </Perm>
    ),
  },
  {
    path: '/reports/profit-loss',
    element: <Navigate to="/finance/reports/profit-loss" replace />,
  },
  {
    path: '/reports/balance-sheet',
    element: <Navigate to="/finance/reports/balance-sheet" replace />,
  },
  {
    path: '/reports/manufacturing',
    element: (
      <P>
        <ManufacturingReportsPage />
      </P>
    ),
  },
  {
    path: '/reports/cost-centers',
    element: (
      <P>
        <CostCenterReportPage />
      </P>
    ),
  },
  {
    path: '/reports/scrap',
    element: (
      <P>
        <ScrapReportPage />
      </P>
    ),
  },
  {
    path: '/inventory/scrap-management',
    element: (
      <P>
        <ScrapManagementPage />
      </P>
    ),
  },
  // Old address of Scrap Management
  { path: '/manufacturing/scrap', element: <Navigate to="/inventory/scrap-management" replace /> },
  {
    path: '/inventory/scrap-yard',
    element: (
      <P>
        <ScrapYardPage />
      </P>
    ),
  },
  {
    path: '/reports/financial',
    element: (
      <P>
        <FinancialReportsPage />
      </P>
    ),
  },
  {
    path: '/reports/gst',
    element: (
      <P>
        <GSTReportsPage />
      </P>
    ),
  },
  {
    path: '/reports/ai-forecasting',
    element: (
      <P>
        <AIForecastingPage />
      </P>
    ),
  },
  {
    path: '/reports/forecasting',
    element: <Navigate to="/reports/ai-forecasting" replace />,
  },


  { path: '*', element: <NotFound /> },
];

export default routes;


// import type { RouteObject } from 'react-router-dom';
// import { Navigate } from 'react-router-dom';
// import { MODULES } from "@/utils/permissions";
// import NotFound from '@/pages/NotFound';
// import ProtectedRoute from '@/components/feature/ProtectedRoute';
// import LoginPage from '@/pages/login/page';
// import ForgetPasswordPage from '@/pages/forgot-password/page';
// import Dashboard from '@/pages/dashboard/page';
// import ReportsLanding from '@/pages/reports/page';
// import Settings from '@/pages/settings/page';
// import Users from '@/pages/users/page';
// import WarehousesPage from '@/pages/masters/warehouses/page';
// import PartiesPage from '@/pages/masters/parties/page';
// import ItemsPage from '@/pages/masters/items/page';
// import CategoriesUnitsPage from '@/pages/masters/categories/page';
// import SalesInvoiceListPage from '@/pages/sales/invoices/page';
// import SalesInvoiceNewPage from '@/pages/sales/invoices/new/page';
// import SaleReturnsPage from '@/pages/sales/returns/page';
// import ChallansPage from '@/pages/sales/challans/page';
// import SalesPaymentPage from '@/pages/sales/payments/page';
// import SalesPaymentsHistoryPage from '@/pages/sales/payments/history/page';
// import PurchasePaymentPage from '@/pages/purchase/payments/page';
// import PurchasePaymentsHistoryPage from '@/pages/purchase/payments/history/page';
// import PurchaseInvoiceListPage from '@/pages/purchase/invoices/page';
// import PurchaseInvoiceNewPage from '@/pages/purchase/invoices/new/page';
// import PurchaseReturnsPage from '@/pages/purchase/returns/page';
// import GRNListPage from '@/pages/purchase/grn/page';
// import PurchaseOrdersPage from '@/pages/purchase/orders/page';
// import PurchaseOrderNewPage from '@/pages/purchase/orders/new/page';
// import StockViewPage from '@/pages/inventory/stock/page';
// import StockReceivingPage from '@/pages/inventory/receiving/page';
// import StockTransferPage from '@/pages/inventory/transfer/page';
// import StockAdjustmentPage from '@/pages/inventory/adjustment/page';
// import StockEntriesPage from '@/pages/inventory/stock-entries/page';
// import BarcodeManagementPage from '@/pages/inventory/barcode-management/page';

// // ── Gate Pass pages ───────────────────────────────────────────────────────────
// import OutwardGatePassPage from '@/pages/inventory/gate-pass/outward/page';
// import InwardGatePassPage from '@/pages/inventory/gate-pass/inward/page';

// // ── Guard pages ───────────────────────────────────────────────────────────────
// import GuardDashboard from '@/pages/guard/dashboard/page';
// import GuardOutwardPage from '@/pages/guard/outward/page';
// import GuardInwardPage from '@/pages/guard/inward/page';

// // Reports
// import StockSummaryReport from '@/pages/reports/stock-summary/page';
// import StockLedgerReport from '@/pages/reports/stock-ledger/page';
// import LowStockReport from '@/pages/reports/low-stock/page';
// import PurchaseRegisterReport from '@/pages/reports/purchase-register/page';
// import GSTPurchaseReport from '@/pages/reports/gst-purchase/page';
// import SalesRegisterReport from '@/pages/reports/sales-register/page';
// import GSTSalesReport from '@/pages/reports/gst-sales/page';
// import OutstandingReport from '@/pages/reports/outstanding/page';
// import DayBookReport from '@/pages/reports/day-book/page';
// import PartyLedgerReport from '@/pages/reports/party-ledger/page';
// import ResetPasswordPage from '@/pages/reset-password/page';

// // ── Route wrappers ────────────────────────────────────────────────────────────

// import { useAuth } from "@/contexts/AuthContext";

// const P = ({
//   children,
//   roles,
//   requirePermission,
// }: {
//   children: React.ReactNode;
//   roles?: string[];
//   requirePermission?: () => boolean;
// }) => {
//   return (
//     <ProtectedRoute
//       requiredRoles={roles}
//       requirePermission={requirePermission}
//     >
//       {children}
//     </ProtectedRoute>
//   );
// };


// /**
//  * Guard-only route — accessible ONLY by SECURITY_GUARD.
//  * Any other role trying to access /guard/* gets redirected to /dashboard.
//  */
// const GuardOnly = ({ children }: { children: React.ReactNode }) => (
//   <ProtectedRoute requiredRoles={['SECURITY_GUARD'] as never}>{children}</ProtectedRoute>
// );

// const routes: RouteObject[] = [
//   { path: '/login', element: <LoginPage /> },
//   { path: '/forgot-password', element: <ForgetPasswordPage /> },
//   { path: '/reset-password/:token', element: <ResetPasswordPage /> },

//   { path: '/', element: <P><Dashboard /></P> },
//   { path: '/settings', element: <P roles={['SUPER_ADMIN']}><Settings /></P> },
//   { path: '/users', element: <P roles={['SUPER_ADMIN', 'SUB_ADMIN']}><Users /></P> },

//   // Masters
//   { path: '/masters', element: <Navigate to="/masters/company" replace /> },
//   // { path: '/masters/warehouses', element: <P roles={['SUPER_ADMIN', 'SUB_ADMIN']}><WarehousesPage /></P> },
//   {
//   path: '/masters/warehouses',
//   element: (
//     <P module={MODULES.WAREHOUSES} action="view">
//       <WarehousesPage />
//     </P>
//     ),
//   },
//  {
//   path: '/masters/parties',
//   element: (
//     <P
//       requirePermission={() =>
//         hasPermission(MODULES.PARTIES, "view")
//       }
//     >
//       <PartiesPage />
//     </P>
//   ),
// },
//   // { path: '/masters/parties', element: <P><PartiesPage /></P> },
//   { path: '/masters/items', element: <P><ItemsPage /></P> },
//   { path: '/masters/categories', element: <P roles={['SUPER_ADMIN', 'SUB_ADMIN']}><CategoriesUnitsPage /></P> },

//   // Sales
//   { path: '/sales', element: <Navigate to="/sales/invoices" replace /> },
//   {
//   path: '/sales/invoices',
//   element: (
//     <P module={MODULES.SALES_INVOICE} action="view">
//       <SalesInvoiceListPage />
//     </P>
//   ),
// }
// ,
//   { path: '/sales/invoices/new', element: <P><SalesInvoiceNewPage /></P> },
//   { path: '/sales/returns', element: <P><SaleReturnsPage /></P> },
//   { path: '/sales/challans', element: <P><ChallansPage /></P> },
//   { path: '/sales/payments', element: <P><SalesPaymentsHistoryPage /></P> },
//   { path: '/sales/payments/new', element: <P><SalesPaymentPage /></P> },

//   // Purchase
//   { path: '/purchase', element: <Navigate to="/purchase/orders" replace /> },
//   { path: '/purchase/orders', element: <P><PurchaseOrdersPage /></P> },
//   { path: '/purchase/orders/new', element: <P><PurchaseOrderNewPage /></P> },
//   { path: '/purchase/invoices', element: <P><PurchaseInvoiceListPage /></P> },
//   { path: '/purchase/invoices/new', element: <P><PurchaseInvoiceNewPage /></P> },
//   { path: '/purchase/payments', element: <P><PurchasePaymentsHistoryPage /></P> },
//   { path: '/purchase/payments/new', element: <P><PurchasePaymentPage /></P> },
//   { path: '/purchase/grn', element: <P><GRNListPage /></P> },
//   { path: '/purchase/returns', element: <P><PurchaseReturnsPage /></P> },

//   // Inventory
//   { path: '/inventory', element: <Navigate to="/inventory/stock" replace /> },
//   { path: '/inventory/stock', element: <P><StockViewPage /></P> },
//   { path: '/inventory/receiving', element: <P><StockReceivingPage /></P> },
//   { path: '/inventory/transfer', element: <P><StockTransferPage /></P> },
//   {
//   path: '/inventory/adjustment',
//   element: (
//     <P module={MODULES.STOCK_ADJUSTMENT} action="update">
//       <StockAdjustmentPage />
//     </P>
//   ),
// }
// ,
//   { path: '/inventory/stock-entries', element: <P><StockEntriesPage /></P> },

//   // ── Gate Pass (under Inventory — non-guard users) ─────────────────────────
//   {
//     path: '/inventory/gate-pass',
//     element: <Navigate to="/inventory/gate-pass/outward" replace />,
//   },
//   {
//     path: '/inventory/gate-pass/outward',
//     element: (
//       <P roles={['SUPER_ADMIN', 'SUB_ADMIN', 'END_USER']}>
//         <OutwardGatePassPage />
//       </P>
//     ),
//   },
//   {
//     path: '/inventory/gate-pass/inward',
//     element: (
//       <P roles={['SUPER_ADMIN', 'SUB_ADMIN', 'END_USER']}>
//         <InwardGatePassPage />
//       </P>
//     ),
//   },

//   // Print
//   { path: '/print', element: <Navigate to="/print/barcode-management" replace /> },
//   { path: '/print/barcode-management', element: <P><BarcodeManagementPage /></P> },
//   // Legacy redirect
//   { path: '/inventory/barcode-management', element: <Navigate to="/print/barcode-management" replace /> },

//   // ── Guard-only routes (/guard/*) ──────────────────────────────────────────
//   // SECURITY_GUARD is redirected here after login.
//   // Non-guard users who try to access /guard/* are blocked by GuardOnly wrapper.
//   { path: '/guard', element: <Navigate to="/guard/dashboard" replace /> },
//   { path: '/guard/dashboard', element: <GuardOnly><GuardDashboard /></GuardOnly> },
//   { path: '/guard/outward', element: <GuardOnly><GuardOutwardPage /></GuardOnly> },
//   { path: '/guard/inward', element: <GuardOnly><GuardInwardPage /></GuardOnly> },

//   // Reports (commented out — uncomment when ready)
//   { path: '/reports', element: <P><ReportsLanding /></P> },
//   { path: '/reports/stock-summary', element: <P><StockSummaryReport /></P> },
//   { path: '/reports/stock-ledger', element: <P><StockLedgerReport /></P> },
//   { path: '/reports/low-stock', element: <P><LowStockReport /></P> },
//   { path: '/reports/purchase-register', element: <P><PurchaseRegisterReport /></P> },
//   { path: '/reports/gst-purchase', element: <P><GSTPurchaseReport /></P> },
//   { path: '/reports/sales-register', element: <P><SalesRegisterReport /></P> },
//   { path: '/reports/gst-sales', element: <P><GSTSalesReport /></P> },
//   { path: '/reports/outstanding', element: <P><OutstandingReport /></P> },
//   { path: '/reports/day-book', element: <P><DayBookReport /></P> },
//   { path: '/reports/party-ledger', element: <P><PartyLedgerReport /></P> },

//   { path: '*', element: <NotFound /> },
// ];

// export default routes;